import axiosInstance from '@/lib/axios';

export interface Client {
  id: string;
  _id?: string;
  name: string;
  industry: string;
  contactPerson?: string;
  email: string;
  phone: string;
  status: 'Active' | 'Inactive' | 'Pending';
  totalDeals: number;
  totalRevenue: number;
  isFavorite: boolean;
  notes?: string;
  createdAt?: string;
  updatedAt?: string;
}

export interface ClientFormData {
  name: string;
  industry: string;
  contactPerson?: string;
  email: string;
  phone: string;
  status: 'Active' | 'Inactive' | 'Pending';
  totalDeals: number;
  totalRevenue: number;
  isFavorite?: boolean;
  notes?: string;
}

export interface ClientStats {
  activeClients: number;
  totalDeals: number;
  totalRevenue: number;
  activeDeals: number;
}

export interface ClientListResponse {
  data: Client[];
  total: number;
  page: number;
  limit: number;
  hasMore?: boolean;
  pagination?: {
    total: number;
    page: number;
    limit: number;
    totalPages: number;
    hasNextPage: boolean;
    hasPrevPage: boolean;
  };
}

export interface ClientFilters {
  page?: number;
  limit?: number;
  search?: string;
  status?: string;
  industry?: string;
  isFavorite?: boolean;
  sort?: string;
  order?: 'asc' | 'desc';
}

export async function getClients(filters: ClientFilters = {}): Promise<ClientListResponse> {
  const params = new URLSearchParams();
  
  if (filters.page) params.append('page', filters.page.toString());
  if (filters.limit) params.append('limit', filters.limit.toString());
  if (filters.search) params.append('search', filters.search);
  if (filters.status) params.append('status', filters.status);
  if (filters.industry) params.append('industry', filters.industry);
  if (filters.isFavorite !== undefined) params.append('isFavorite', filters.isFavorite.toString());
  if (filters.sort) params.append('sort', filters.sort);
  if (filters.order) params.append('order', filters.order);

  const response = await axiosInstance.get(`/clients?${params.toString()}`);
  return response.data;
}

export async function getClientById(id: string): Promise<Client> {
  const response = await axiosInstance.get(`/clients/${id}`);
  return response.data;
}

export async function createClient(clientData: ClientFormData): Promise<Client> {
  try {
    console.log('Creating client with data:', clientData);
    const response = await axiosInstance.post('/clients', clientData);
    console.log('Client created successfully:', response.data);
    return response.data;
  } catch (error: any) {
    console.error('Error creating client:', error);
    console.error('Request data:', clientData);
    console.error('Response status:', error.response?.status);
    console.error('Response data:', error.response?.data);
    throw error;
  }
}

export async function updateClient(id: string, clientData: Partial<ClientFormData>): Promise<Client> {
  const response = await axiosInstance.patch(`/clients/${id}`, clientData);
  return response.data;
}

export async function deleteClient(id: string): Promise<{ message: string; deletedClient: Client }> {
  const response = await axiosInstance.delete(`/clients/${id}`);
  return response.data;
}

export async function toggleClientFavorite(id: string): Promise<Client> {
  const response = await axiosInstance.patch(`/clients/${id}/toggle-favorite`);
  return response.data;
}

export async function getClientStats(): Promise<ClientStats> {
  const response = await axiosInstance.get('/clients/stats');
  return response.data;
}

export const clientService = {
  getClients,
  getClientById,
  createClient,
  updateClient,
  deleteClient,
  toggleClientFavorite,
  getClientStats,
};
